import React from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { AbsenceCateringChoice } from '@/components/AbsenceApplication/AbsenceCateringChoice';
import { AbsenceDateField } from '@/components/AbsenceApplication/AbsenceDateField';
import { useAbsenceApplicationForm } from '@/components/AbsenceApplication/hooks/useAbsenceApplicationForm';
import {
  DateField,
  type NonAttendanceApplicationDetail,
  type NonAttendanceApplicationProperties,
  type SubmitNonAttendanceApplicationRequest,
} from '@/components/AbsenceApplication/types';
import { Button } from '@/components/Button';
import { CalendarSheet } from '@/components/CalendarSheet';
import { ArrowLeft } from '@/components/Icons/ArrowLeft';
import { ChevronLeft } from '@/components/Icons/ChevronLeft';
import { Close } from '@/components/Icons/Close';
import {
  BorderRadius,
  ButtonType,
  ButtonVariant,
  Size,
} from '@/types/components';

interface Props {
  properties: NonAttendanceApplicationProperties;
  onClose: () => void;
  onBack: () => void;
  onOverlap: () => void;
  onSuccess: (
    application: NonAttendanceApplicationDetail,
    payload: SubmitNonAttendanceApplicationRequest,
  ) => void;
  onServerError: (payload: SubmitNonAttendanceApplicationRequest) => void;
}

export const AbsenceApplicationForm: React.FC<Props> = ({
  properties,
  onClose,
  onBack,
  onOverlap,
  onSuccess,
  onServerError,
}) => {
  const { t } = useTranslation();
  const {
    control,
    register,
    errors,
    isPending,
    currentProfile,
    profileClassAndSchool,
    scrollProgress,
    handleScroll,
    datePickerRef,
    calendarField,
    calendarRange,
    openCalendar,
    closeCalendar,
    selectCalendarDate,
    onSubmit,
  } = useAbsenceApplicationForm({
    properties,
    onOverlap,
    onSuccess,
    onServerError,
  });

  const { isCateringEnabled, isRigaCateringEnabled } = properties;
  const { filledDate, outlinedDate, minDate, maxDate } = calendarRange;

  return (
    <form onSubmit={onSubmit} className="flex h-full flex-col">
      <div className="relative flex shrink-0 items-center justify-between bg-background px-4 pt-[calc(16px+env(safe-area-inset-top))] pb-3 md:px-5 md:pt-5 md:pb-4">
        <div className="flex min-w-0 items-center gap-3">
          <button
            type="button"
            onClick={onBack}
            aria-label={t('global.back')}
            className="flex size-6 shrink-0 cursor-pointer items-center justify-center text-foreground md:hidden"
          >
            <ArrowLeft />
          </button>
          <button
            type="button"
            onClick={onBack}
            aria-label={t('global.back')}
            className="hidden size-5 shrink-0 cursor-pointer items-center justify-center text-muted-foreground md:flex"
          >
            <ChevronLeft className="size-4" />
          </button>
          <h2 className="text-headline-20 hidden truncate text-foreground md:block">
            {t('absenceApplication.title')}
          </h2>
        </div>
        <h2
          style={{ opacity: scrollProgress }}
          className="text-callout-16 pointer-events-none absolute left-1/2 -translate-x-1/2 text-foreground transition-opacity duration-75 md:hidden"
        >
          {t('absenceApplication.title')}
        </h2>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('global.close')}
          className="hidden size-6 shrink-0 cursor-pointer items-center justify-center text-foreground md:flex"
        >
          <Close />
        </button>
      </div>

      <div
        onScroll={handleScroll}
        className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-4 pb-4 md:px-5"
      >
        <h2
          style={{ opacity: 1 - scrollProgress }}
          className="text-title-48 text-foreground transition-opacity duration-75 md:hidden"
        >
          {t('absenceApplication.title')}
        </h2>

        {currentProfile && (
          <div className="flex flex-col gap-[3px] rounded-xl bg-card px-3 py-2 shadow-card md:hidden">
            <p className="text-subhead-14 truncate text-foreground">
              {currentProfile.user.firstName} {currentProfile.user.lastName}
            </p>
            {profileClassAndSchool && (
              <p className="text-body-13 truncate text-muted-foreground">
                {profileClassAndSchool}
              </p>
            )}
          </div>
        )}

        <div ref={datePickerRef} className="relative flex flex-col">
          <div className="flex gap-3">
            <Controller
              control={control}
              name={DateField.From}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <AbsenceDateField
                  label={t('absenceApplication.from')}
                  value={field.value}
                  hasError={!!fieldState.error}
                  onClick={() => openCalendar(DateField.From)}
                />
              )}
            />
            <Controller
              control={control}
              name={DateField.To}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <AbsenceDateField
                  label={t('absenceApplication.to')}
                  value={field.value}
                  hasError={!!fieldState.error}
                  onClick={() => openCalendar(DateField.To)}
                />
              )}
            />
          </div>

          {errors[DateField.To] && (
            <p className="mt-2 text-body-14 text-destructive">
              {t('absenceApplication.errors.missingEndDate')}
            </p>
          )}

          <CalendarSheet
            isOpen={calendarField !== null}
            filledDate={filledDate}
            outlinedDate={outlinedDate}
            minDate={minDate}
            maxDate={maxDate}
            popoverClassName="inset-x-0 top-full mt-3"
            onChange={selectCalendarDate}
            onClose={closeCalendar}
          />
        </div>

        {isCateringEnabled && (
          <Controller
            control={control}
            name="cateringChoice"
            rules={{ required: true }}
            render={({ field, fieldState }) => (
              <div className="flex flex-col">
                <AbsenceCateringChoice
                  value={field.value}
                  showNote={isRigaCateringEnabled}
                  onChange={field.onChange}
                />
                {fieldState.error && (
                  <p className="mt-2 text-body-14 text-destructive">
                    {t('absenceApplication.errors.missingCatering')}
                  </p>
                )}
              </div>
            )}
          />
        )}

        <div className="flex flex-col">
          <textarea
            {...register('reason', { required: true })}
            placeholder={t('absenceApplication.reasonPlaceholder')}
            className={`min-h-[148px] resize-none rounded-xl border bg-on-primary-surface px-4 py-3 text-body-16 text-foreground outline-none placeholder:text-subtle-foreground ${
              errors.reason ? 'border-destructive' : 'border-border'
            }`}
          />
          {errors.reason && (
            <p className="mt-2 text-body-14 text-destructive">
              {t('absenceApplication.errors.missingReason')}
            </p>
          )}
        </div>
      </div>

      <div className="flex shrink-0 flex-col items-center gap-3 bg-background px-4 pt-3 pb-[calc(20px+env(safe-area-inset-bottom))] md:px-5 md:pb-5">
        <Button
          type={ButtonType.Submit}
          variant={ButtonVariant.Primary}
          size={Size.Large}
          radius={BorderRadius.Small}
          isLoading={isPending}
          className="w-full"
        >
          {t('absenceApplication.submit')}
        </Button>
      </div>
    </form>
  );
};
